import { memo, useState } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";

import { Paragraph, Heading } from "common/core/typography";
import { ChoiceChip, RadioGroup, RadioInput } from "common/core/form/option";
import StampRight from "assets/images/stamp-right.svg";
import { useMutation } from "util/graphql";
import { NotaryActivationStatus } from "graphql_globals";
import Button from "common/core/button";
import WorkflowModal from "common/modals/workflow_modal";
import Svg from "common/core/svg";

import Styles from "./index.module.scss";
import OptingOnDemandCapacity from "./opting_on_demand_capacity_mutation.graphql";

type Props = {
  onNext: () => void;
  optedIn: boolean;
};

const MESSAGES = defineMessages({
  modalLabel: {
    id: "7ff06969-82bf-4210-afa0-6fbfa0ff0ce6",
    defaultMessage: "Opt in to on-demand",
  },
});

function OnDemandOptInModal(props: Props) {
  const intl = useIntl();
  const { onNext, optedIn } = props;
  const [optedInStatus, setOptedInStatus] = useState<NotaryActivationStatus>(
    optedIn ? NotaryActivationStatus.OPTED_IN : NotaryActivationStatus.INVITED,
  );
  const optingOnDemandCapacityMutateFn = useMutation(OptingOnDemandCapacity);
  const handleContinue = () => {
    optingOnDemandCapacityMutateFn({
      variables: {
        input: {
          activationStatus: optedInStatus,
        },
      },
    }).finally(onNext);
  };

  return (
    <WorkflowModal
      automationId="opt-in-modal"
      aria-label={intl.formatMessage(MESSAGES.modalLabel)}
      className={Styles.modal}
      footerSeparator={false}
      buttons={[
        <Button
          key="opt-in"
          buttonColor="action"
          variant="primary"
          fullwidth
          onClick={handleContinue}
          data-automation-id="opt-in-continue"
        >
          <FormattedMessage id="3f5f9654-bfac-4909-a980-a2ad2be59793" defaultMessage="Continue" />
        </Button>,
      ]}
      closeBehavior={{ tag: "with-button", onClose: onNext }}
      footerText={
        <FormattedMessage
          id="0f15af3d-41a7-4a3c-a58d-0f4cc6376095"
          defaultMessage="Update this later from Settings > Personal Information"
        />
      }
    >
      <>
        <div className="m-200">
          <Svg src={StampRight} alt={""} width={86} />
        </div>
        <Heading level="h1" textStyle="subtitle" className="my-200">
          <FormattedMessage
            id="bf933564-bacd-4f0d-a0b7-4924c5905f8e"
            defaultMessage="On-demand is available in your state!"
          />
        </Heading>
        <Paragraph className="my-200">
          <FormattedMessage
            id="82337080-e21a-432b-9537-b54072b10e57"
            defaultMessage="The on-demand Notarize Network brings notarization requests directly to you. By joining, you can take calls when you want to fit your own schedule. This network is only available to notaries in a few states."
          />
        </Paragraph>
        <div className="my-200">
          <RadioGroup
            horizontal
            label={
              <FormattedMessage
                id="386e830d-8306-4e8d-b27f-93ea4e86bfd8"
                defaultMessage="Join the On-Demand Notarize Network?"
              />
            }
          >
            <ChoiceChip
              data-automation-id="opt-in-true"
              label={
                <FormattedMessage
                  id="144e838b-711a-4d12-9676-8bd5b3ee3908"
                  defaultMessage="Yes, opt me in"
                />
              }
              radio={
                <RadioInput
                  value={NotaryActivationStatus.OPTED_IN}
                  checked={optedInStatus === NotaryActivationStatus.OPTED_IN}
                  onClick={() => setOptedInStatus(NotaryActivationStatus.OPTED_IN)}
                />
              }
            />
            <ChoiceChip
              data-automation-id="opt-in-false"
              label={
                <FormattedMessage
                  id="a76cbf0e-3937-4c79-82b3-7b3d401472ea"
                  defaultMessage="No, maybe later"
                />
              }
              radio={
                <RadioInput
                  value={NotaryActivationStatus.INVITED}
                  checked={optedInStatus === NotaryActivationStatus.INVITED}
                  onClick={() => setOptedInStatus(NotaryActivationStatus.INVITED)}
                />
              }
            />
          </RadioGroup>
        </div>
      </>
    </WorkflowModal>
  );
}

export default memo(OnDemandOptInModal);
